import { ref, computed, provide, inject, watch, nextTick } from "vue"
import { BACKGROUND_PRESETS, type BackgroundPattern } from "@/themes/backgroundPresets"
import { api } from "@/lib/api"
import { user } from "@/composables/auth/useAuth"
import { useProfile } from "@/composables/profile/useProfile"

const KEY = Symbol("background-pattern")

let profileLoadAttempted = false

export type BackgroundState = {
  patterns: BackgroundPattern[]
  currentKey: string
  setCurrentKey: (k: string) => Promise<void>
  loadBackgroundFromProfile: (backgroundKey: string | null) => void
}

function loadPersisted<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function persist<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {}
}

export function provideBackgroundPattern() {
  const stored = loadPersisted("background-state", {
    currentKey: BACKGROUND_PRESETS[0]?.key || "none",
  })

  debug("🎨 Background pattern provider initialized with:", stored)

  const currentKey = ref<string>(stored.currentKey as string)
  const { profileData, loaded } = useProfile()

  const patterns = computed<BackgroundPattern[]>(() => BACKGROUND_PRESETS)

  const current = computed<BackgroundPattern>(() => {
    return (
      patterns.value.find((p) => p.key === currentKey.value) ??
      (BACKGROUND_PRESETS[0] as BackgroundPattern)
    )
  })

  watch(
    () => current.value,
    (pattern) => {
      debug("👀 Current pattern changed, applying:", pattern.key)
      // Use nextTick to ensure DOM is ready
      nextTick(() => {
        applyBackgroundClass(pattern)
      })
    },
    { immediate: true },
  )

  // Watch for profile data to load background from profile
  // This replaces the user watcher to avoid duplicate API calls
  watch(
    () => profileData.value?.background,
    (background) => {
      // Only load if profile is loaded and we have a background value
      if (loaded.value && background && !profileLoadAttempted) {
        profileLoadAttempted = true
        debug("🎨 Loading background from cached profile data:", background)
        loadBackgroundFromProfile(background)
      }
    },
    { immediate: true },
  )

  // Reset flag when user logs out
  watch(user, (newUser) => {
    if (!newUser) {
      profileLoadAttempted = false
    }
  })

  async function setCurrentKey(k: string) {
    currentKey.value = k

    // If user is authenticated, save to database
    if (user.value) {
      try {
        await api("updateProfile", { background: k })
      } catch (error) {
        logError("Failed to save background to database:", error)
      }
    }

    // Always persist to localStorage as fallback
    persistState()
  }

  function loadBackgroundFromProfile(backgroundKey: string | null) {
    debug("🎨 Loading background from profile:", backgroundKey)
    if (backgroundKey && BACKGROUND_PRESETS.some((p) => p.key === backgroundKey)) {
      debug("✅ Valid background key, setting:", backgroundKey)
      currentKey.value = backgroundKey
      // Persist to localStorage so it's available on next page load
      persistState()
    } else {
      debug("❌ Invalid background key or not found in presets")
    }
  }

  function persistState() {
    persist("background-state", {
      currentKey: currentKey.value,
    })
  }

  const reactiveState = {
    get patterns() {
      return patterns.value
    },
    get currentKey() {
      return currentKey.value
    },
    setCurrentKey,
    loadBackgroundFromProfile,
  } as BackgroundState

  provide(KEY, reactiveState)
  return reactiveState
}

export function useBackgroundPattern() {
  const s = inject<BackgroundState>(KEY)
  if (!s) throw new Error("useBackgroundPattern must be used after provideBackgroundPattern()")
  return s
}

function applyBackgroundClass(pattern: BackgroundPattern) {
  const appShell = document.querySelector(".app-shell")
  if (!appShell) {
    warn("⚠️ .app-shell element not found, cannot apply background")
    return
  }

  debug("🎨 Applying background class:", pattern.className, "for key:", pattern.key)

  // Remove all background classes
  BACKGROUND_PRESETS.forEach((preset) => {
    appShell.classList.remove(preset.className)
  })

  // Add the current background class
  if (pattern.className && pattern.key !== "none") {
    appShell.classList.add(pattern.className)
    debug("✅ Background class applied:", pattern.className)
  } else {
    debug("ℹ️ No background class (pattern is 'none' or has no className)")
  }
}
import { debug, warn, error as logError } from "@/utils/debug"
